import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ContextService } from '@lr/core';

/**
 * 403 on these URLs must not navigate to Forbidden (shell/widget calls, not pages).
 * Add a matcher when a new global request would otherwise kick the user to /access-denied.
 */
const FORBIDDEN_PAGE_EXCEPTIONS: ((url: string) => boolean)[] = [
  url => url.includes('/me/notifications'),
  // GET /client/{mcode}/subclients (mCode varies). Nested /subclients/{ccode}/... does not end here.
  url => {
    const path = url.split('?')[0];
    return path.endsWith('/subclients') || path.endsWith('/subclients/');
  }
];

/** Gateway code for a valid login with no active portal user (PortalAccessMiddleware.PortalUserNotFoundCode). */
const PORTAL_USER_NOT_FOUND = 'PORTAL_USER_NOT_FOUND';

/**
 * HR-only. Status only (no JWT inspect).
 * 401 or Auth0 dead session → /unauthorized (fullscreen, session is dead).
 * 403 on dashboard / login callback → /access-denied (fullscreen, no menu).
 * 403 on other pages → /dashboard (history.back() can land on another forbidden page).
 * Must wrap authInterceptor so getAccessTokenSilently failures are visible here.
 */
export const hrAuthErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const contextService = inject(ContextService);

  return next(req).pipe(
    catchError(error => {
      // No active portal user anymore (for example inactivated). Applies to exempt URLs too.
      if (error.status === 403 && error.error?.code === PORTAL_USER_NOT_FOUND) {
        showAccessDenied(router);
        return throwError(() => error);
      }

      if (skipsForbiddenPage(req)) {
        return throwError(() => error);
      }

      if (error.status === 401 || isAuth0SessionDead(error)) {
        contextService.rejectSession();
        void router.navigate(['/unauthorized'], { replaceUrl: true });
      } else if (error.status === 403) {
        handleForbidden(router);
      }

      return throwError(() => error);
    })
  );
};

function showAccessDenied(router: Router): void {
  const path = router.url.split('?')[0];
  if (path.endsWith('/access-denied') || path.endsWith('/unauthorized') || path.endsWith('/login')) {
    return;
  }
  void router.navigate(['/access-denied'], { replaceUrl: true });
}

function skipsForbiddenPage(req: HttpRequest<unknown>): boolean {
  return FORBIDDEN_PAGE_EXCEPTIONS.some(matches => matches(req.url));
}

function handleForbidden(router: Router): void {
  const path = router.url.split('?')[0];
  if (path.endsWith('/access-denied') || path.endsWith('/unauthorized') || path.endsWith('/login')) {
    return;
  }

  // Home / login callback: nowhere useful to go back. Fullscreen 403 (no menu).
  if (isDashboardPath(path) || path.includes('/callback')) {
    void router.navigate(['/access-denied'], { replaceUrl: true });
    return;
  }

  void router.navigate(['/dashboard'], { replaceUrl: true });
}

function isDashboardPath(path: string): boolean {
  const normalized = path.replace(/\/$/, '') || '/';
  return normalized === '/' || normalized === '/dashboard' || normalized.endsWith('/dashboard');
}

function isAuth0SessionDead(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const code = (error as { error?: unknown }).error;
  return code === 'login_required'
    || code === 'invalid_grant'
    || code === 'missing_refresh_token'
    || code === 'consent_required';
}
